import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text
} from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useInvalidateDeploymentList,
  useStartDeployment
} from '@/queries/deploymentQueries'
import { showToast } from '@/theme/notifications'

type DeploymentRestartConfirmProps = {
  opened: boolean
  deploymentId: string
  onClose: () => void
}

export const DeploymentRestartConfirm = ({
  opened,
  deploymentId,
  onClose
}: DeploymentRestartConfirmProps) => {
  const intl = useIntl()

  const { mutateAsync: startDeployment, isPending } = useStartDeployment()

  const { invalidateDeploymentList } = useInvalidateDeploymentList()

  const handleRestartDeployment = async () => {
    try {
      await startDeployment(deploymentId)

      showToast(
        intl.formatMessage({ id: 'deployments.restart.inProgress' }),
        'info'
      )

      void invalidateDeploymentList()
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'deployments.restart.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="deployments.restart" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Stack gap="xl">
          <Text size="sm">
            <FormattedMessage id="deployments.restart.confirmation" />
          </Text>

          <Group justify="end">
            <Button miw={120} variant="default" onClick={onClose}>
              <FormattedMessage id="cancel" />
            </Button>

            <Button miw={120} onClick={() => void handleRestartDeployment()}>
              <FormattedMessage id="deployments.restart" />
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
